import { useCallback, useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { Camera, Clock3, MapPin, Loader2, Download, Plus, Trash2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { todayLocal } from "@/lib/periods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import QrScanner from "@/components/QrScanner";
import SelfieCapture from "@/components/SelfieCapture";
import { Link } from "react-router-dom";
import {
  getMyShift, clockWork, deviceId, getPunchSelfie, getWorkDevices, approveWorkDevice, removeWorkDevice, getWorkStaff, setWorkLocation, setWorkShifts,
  getWorkDay, getWorkMonth, addWorkPunch, deleteWorkPunch, getScanFailures, getEveryoneToday, getCheckinTrends, setWorkPerson, getQcDay,
  getRoster, setRosterSession, getRosterDay, setRosterDay, undoRosterDay,
} from "@/lib/api";

// Staff clock-in. Everyone: clock in / out on their phone, accepted only at the gym.
// Facility heads: today's board (with that day's coach swaps), the weekly session roster that
// decides who is expected when, month totals for salary, other shifts, and the gym's location.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const field = "bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100";
const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

const GOOD_ENOUGH_M = 150;   // the server refuses a location rougher than this (MAX_ACCURACY_M)

// The phone's location. Indoors the first fix is often rough (±300 m+), so keep listening for up to
// 15 s and take the best one, stopping as soon as it's good enough (22 Oct). Some iPhones (inside
// WhatsApp's browser) first give an old or wrong place that claims to be precise (CR-0075), so a
// cached reading is skipped, we listen at least MIN_LISTEN_MS, and every fresh reading goes to the
// server, which accepts the clock-in if any of them is at the gym.
const MIN_LISTEN_MS = 5000;
function position() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("This phone or browser can't share its location."));
    const started = Date.now();
    let best = null;
    let stale = null;
    const fixes = [];
    let id = null;
    const done = () => {
      navigator.geolocation.clearWatch(id);
      clearTimeout(timer);
      const pick = best || stale;
      if (pick) resolve({ ...pick, fixes: fixes.slice(-10) });
      else reject(new Error("Couldn't get your location. Step near a window and try again."));
    };
    const timer = setTimeout(done, 15000);
    id = navigator.geolocation.watchPosition(
      (p) => {
        const fix = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
        if (p.timestamp && p.timestamp < started - 1000) { stale = stale || fix; return; }   // a cached reading
        if (!fixes.some((f) => f.lat === fix.lat && f.lng === fix.lng)) fixes.push(fix);
        if (!best || fix.accuracy < best.accuracy) best = fix;
        if (best.accuracy <= GOOD_ENOUGH_M / 2 && (fixes.length >= 3 || Date.now() - started >= MIN_LISTEN_MS)) done();
      },
      (e) => {
        if (e.code === 1) {
          navigator.geolocation.clearWatch(id); clearTimeout(timer);
          reject(new Error("Location is blocked. Allow location for this site in your browser settings, then try again."));
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

function Badge({ tone, children }) {
  const tones = { rose: "border-rose-500/40 bg-rose-500/10 text-rose-300", amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", zinc: "border-[#2a2a2a] text-zinc-400" };
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] ${tones[tone]}`}>{children}</span>;
}

function DayFlags({ r }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {r.late.map((l) => <Badge key={l.shift} tone="amber">Late {l.minutes} min · {l.shift}</Badge>)}
      {r.missed.map((m) => <Badge key={m.shift} tone="rose">Missed {m.shift}</Badge>)}
      {r.forgot_out && <Badge tone="rose">Didn't clock out</Badge>}
      {r.clocked_in && <Badge tone="emerald">At work now</Badge>}
    </div>
  );
}

function MyShift() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanFor, setScanFor] = useState(null);       // "in" | "out" while the camera is open
  const [pos, setPos] = useState(null);               // the phone's location, taken just before scanning
  const [selfieFor, setSelfieFor] = useState(null);   // "in" | "out" while the front camera is open
  const [selfie, setSelfie] = useState(null);
  const load = useCallback(() => getMyShift().then(setMe).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async (kind, extra) => {
    setBusy(true);
    try {
      const r = await clockWork({ kind, ...extra });
      toast.success(`${kind === "in" ? "Clocked in" : "Clocked out"} at ${r.gym}`);
      load();
    } catch (e) { toast.error(err(e, "Couldn't clock in")); } finally { setBusy(false); }
  };
  // Location first (asks for permission), then a selfie (front camera), then the desk QR (back camera).
  // The QR and the location both have to match this gym, on the person's own phone.
  const clock = async (kind) => {
    if (me?.remote) { setSelfieFor(kind); return; }      // remote staff: selfie on their own phone or laptop, no QR
    setBusy(true);
    try { setPos(await position()); setSelfieFor(kind); } catch (e) { toast.error(err(e, "Couldn't get your location")); } finally { setBusy(false); }
  };
  const onSelfie = useCallback(async (img) => {
    const k = selfieFor; setSelfie(img); setSelfieFor(null);
    if (!me?.remote) { setScanFor(k); return; }
    let where = {};
    try { where = await Promise.race([position(), new Promise((_, no) => setTimeout(() => no(new Error("slow")), 4000))]); } catch { where = {}; }
    send(k, { remote: true, selfie: img, device_id: deviceId(), ...where });
  }, [selfieFor, me]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeSelfie = useCallback(() => setSelfieFor(null), []);
  const onScan = useCallback((text) => {
    const k = scanFor; setScanFor(null);
    send(k, { qr: text, ...pos, selfie, device_id: deviceId() });
  }, [scanFor, pos, selfie]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeScan = useCallback(() => setScanFor(null), []);
  if (!me) return <div className="card-surface p-6 text-sm text-zinc-500">Loading…</div>;
  const atWork = !!me.open;
  const t = me.today;
  return (
    <div className="card-surface p-5 space-y-4" data-testid="my-shift">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">My shift · {me.gym}</div>
          <div className="text-sm text-zinc-400 mt-1">
            {t.shifts.length ? `Today: ${t.shifts.join(", ")}` : "No sessions or shifts for you today"}
          </div>
        </div>
        {atWork && <Badge tone="emerald">At work since {new Date(me.open.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Badge>}
      </div>
      {!me.remote && !me.location_set && <div className="text-xs text-amber-300">This gym's location isn't set yet, so clock-in won't work here. Your facility head sets it once, standing at the gym (Gym location tab).</div>}
      <Button onClick={() => clock(atWork ? "out" : "in")} disabled={busy}
        className={`w-full h-14 text-lg rounded-xl ${atWork ? "bg-zinc-700 hover:bg-zinc-600" : "bg-emerald-600 hover:bg-emerald-500"}`}
        data-testid={atWork ? "clock-out-btn" : "clock-in-btn"}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clock3 className="h-5 w-5 mr-2" />}
        {me.remote ? (atWork ? "Check out" : "Check in") : (atWork ? "Clock out" : "Clock in")}
      </Button>
      <div className="text-[11px] text-zinc-500">{me.remote
        ? "You work remotely: take a quick selfie on your own phone or laptop to check in. Everyone checks in every working day; if you don't, your supervisor is told."
        : "Allow location, take a quick selfie, then scan the QR on the front-desk tablet. Use your own phone: the QR and your location both have to match this gym, and your location is only used to check you're here."}</div>
      {selfieFor && <SelfieCapture title={selfieFor === "in" ? "Clock in · selfie" : "Clock out · selfie"} hint="Your facility head sees this photo with the entry" onResult={onSelfie} onClose={closeSelfie} />}
      {scanFor && <QrScanner title={scanFor === "in" ? "Clock in" : "Clock out"} hint="Point at the QR on the front-desk tablet" onResult={onScan} onClose={closeScan} />}
      {t.punches.length > 0 && (
        <div className="text-sm text-zinc-300">Today: {t.punches.map((p) => `${p.kind === "in" ? "in" : "out"} ${p.time}`).join(" · ")} · {t.hours} h</div>
      )}
      <DayFlags r={t} />
    </div>
  );
}

function Today({ staff }) {
  const askDelete = useDeleteAsk("manager");
  const [day, setDay] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [fix, setFix] = useState({ user_id: "", time: "", kind: "in", note: "" });
  const [noScan, setNoScan] = useState([]);
  const load = useCallback(() => {
    getWorkDay(day).then(setData).catch((e) => toast.error(err(e, "Couldn't load")));
    getScanFailures("staff", day).then((r) => setNoScan(r.people || [])).catch(() => setNoScan([]));
  }, [day]);
  useEffect(() => { load(); }, [load]);
  const clockInForThem = async (p) => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    try {
      await addWorkPunch({ user_id: p.person_id, date: day, time, kind: "in", note: `Couldn't scan: ${p.reason}`.slice(0, 200) });
      toast.success(`${p.name} clocked in`); load();
    } catch (e) { toast.error(err(e, "Couldn't clock them in")); }
  };
  const add = async () => {
    try {
      await addWorkPunch({ ...fix, date: day });
      toast.success("Added"); setFix({ user_id: "", time: "", kind: "in", note: "" }); load();
    } catch (e) { toast.error(err(e, "Couldn't add")); }
  };
  const [selfieImg, setSelfieImg] = useState(null);
  const showSelfie = async (id) => {
    try { setSelfieImg((await getPunchSelfie(id)).image); } catch (e) { toast.error(err(e, "Couldn't load the selfie")); }
  };
  const remove = async (id) => {
    const reason = askDelete("this clock-in/out", "Remove this clock-in/out?");
    if (reason === null) return;
    try { if (!deletionRequested(await deleteWorkPunch(id, reason))) load(); } catch (e) { toast.error(err(e, "Couldn't remove")); }
  };
  return (
    <div className="space-y-3">
      <div className="w-44"><DatePicker value={day} onChange={(e) => setDay(e.target.value)} data-testid="work-day" /></div>
      {noScan.length > 0 && (
        <div className="card-surface p-4 space-y-2" data-testid="staff-no-scan">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-300"><ScanLine className="h-4 w-4" /> Couldn&apos;t scan {day === todayLocal() ? "today" : "this day"}</div>
          {noScan.map((p) => (
            <div key={p.person_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1a1a] pt-2">
              <div className="min-w-0">
                <div className="text-sm text-zinc-100">{p.name}{p.got_in && <span className="ml-2 text-xs text-emerald-400">got in later</span>}</div>
                <div className="text-[11px] text-zinc-500">
                  {new Date(p.last_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{p.tries > 1 ? ` · ${p.tries} tries` : ""} · {p.reason}
                </div>
              </div>
              {!p.got_in && day === todayLocal() && (
                <Button size="sm" onClick={() => clockInForThem(p)} data-testid={`clock-in-for-${p.person_id}`}>Clock in now</Button>
              )}
            </div>
          ))}
          <div className="text-[11px] text-zinc-500">Clocking someone in here marks it as added by hand, with the reason they couldn&apos;t scan.</div>
        </div>
      )}
      <DaySessions day={day} staff={staff} onChanged={load} />
      {selfieImg && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6" onClick={() => setSelfieImg(null)} role="dialog" aria-label="Selfie">
          <img src={selfieImg} alt="Selfie taken with the clock-in" className="max-h-[80vh] max-w-full rounded-xl" />
        </div>
      )}
      {(data?.rows || []).length === 0 && <div className="card-surface p-6 text-sm text-zinc-500">Nobody scheduled or clocked in on this day.</div>}
      {(data?.rows || []).map((r) => (
        <div key={r.user_id} className="card-surface p-4 space-y-2" data-testid={`work-row-${r.user_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium">{r.name}</div>
            <div className="font-mono text-sm text-zinc-300">{r.hours} h</div>
          </div>
          <div className="text-xs text-zinc-500">Shifts: {r.shifts.length ? r.shifts.join(", ") : "none set"}</div>
          <div className="flex flex-wrap gap-1.5">
            {r.records.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1 rounded-md border border-[#262626] px-2 py-0.5 text-xs text-zinc-300">
                {p.kind === "in" ? "In" : "Out"} {new Date(p.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                {p.source === "manual" && <span className="text-amber-300" title={`${p.note} (${p.by})`}>· added by hand</span>}
                {p.has_selfie && <button onClick={() => showSelfie(p.id)} className="text-blue-400 hover:text-blue-300" aria-label="See selfie" data-testid={`selfie-${p.id}`}><Camera className="h-3 w-3" /></button>}
                {p.new_phone && <span className="text-amber-300" title="First clock-in on this phone: it became their registered phone">· new phone</span>}
                <button onClick={() => remove(p.id)} className="text-zinc-600 hover:text-rose-400" aria-label="Remove"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <DayFlags r={r} />
        </div>
      ))}
      <div className="card-surface p-4 space-y-2">
        <div className="text-sm font-medium">Fix a forgotten clock-in or clock-out</div>
        <div className="flex flex-wrap gap-2">
          <select value={fix.user_id} onChange={(e) => setFix({ ...fix, user_id: e.target.value })} className={field} data-testid="fix-user">
            <option value="">Who?</option>
            {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
          </select>
          <select value={fix.kind} onChange={(e) => setFix({ ...fix, kind: e.target.value })} className={field}>
            <option value="in">Clock in</option><option value="out">Clock out</option>
          </select>
          <input type="time" value={fix.time} onChange={(e) => setFix({ ...fix, time: e.target.value })} className={field} />
          <Input value={fix.note} onChange={(e) => setFix({ ...fix, note: e.target.value })} placeholder="Reason (e.g. phone was dead)" className="bg-[#0a0a0a] border-[#262626] flex-1 min-w-[180px]" />
          <Button onClick={add} disabled={!fix.user_id || !fix.time || fix.note.trim().length < 3} className="bg-blue-600 hover:bg-blue-500">Add</Button>
        </div>
        <div className="text-[11px] text-zinc-500">Added by hand entries show your name and the reason, and go in the audit log.</div>
      </div>
    </div>
  );
}

function DaySessions({ day, staff, onChanged }) {
  const askDelete = useDeleteAsk("manager");
  const [data, setData] = useState(null);
  const [swap, setSwap] = useState(null);           // {id, user_id, note}
  const load = useCallback(() => getRosterDay(day).then(setData).catch(() => {}), [day]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await setRosterDay({ day, session_schedule_id: swap.id, user_ids: swap.user_id ? [swap.user_id] : [], note: swap.note });
      toast.success("Changed for this day"); setSwap(null); load(); onChanged();
    } catch (e) { toast.error(err(e, "Couldn't change")); }
  };
  const undo = async (id) => {
    const reason = askDelete("this day's change");
    if (reason === null) return;
    try { if (deletionRequested(await undoRosterDay(day, id, reason))) return; load(); onChanged(); }
    catch (e) { toast.error(err(e, "Couldn't undo")); }
  };
  if (!data?.sessions?.length) return null;
  return (
    <div className="card-surface p-4 space-y-2" data-testid="day-sessions">
      <div className="text-sm font-medium">Sessions on this day</div>
      {data.sessions.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono w-12 text-zinc-400">{s.start_time}</span>
          <span className="text-zinc-300">{s.label}</span>
          <span className={s.coaches.length ? "text-zinc-100" : "text-rose-300"}>· {s.coaches.join(", ") || "no coach"}</span>
          {s.changed && <Badge tone="amber">changed{s.note ? `: ${s.note}` : ""}</Badge>}
          {swap?.id === s.id ? (
            <span className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <select value={swap.user_id} onChange={(e) => setSwap({ ...swap, user_id: e.target.value })} className={field}>
                <option value="">No coach</option>
                {staff.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
              </select>
              <Input value={swap.note} onChange={(e) => setSwap({ ...swap, note: e.target.value })} placeholder="Why (e.g. on leave)" className="bg-[#0a0a0a] border-[#262626] w-40" />
              <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-500">Save</Button>
              <button onClick={() => setSwap(null)} className="text-xs text-zinc-500">Cancel</button>
            </span>
          ) : (
            <button onClick={() => setSwap({ id: s.id, user_id: s.user_ids[0] || "", note: "" })} className="text-xs text-blue-400">Change for this day</button>
          )}
          {s.changed && swap?.id !== s.id && <button onClick={() => undo(s.id)} className="text-xs text-zinc-500">Undo</button>}
        </div>
      ))}
    </div>
  );
}

function Roster({ staff }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => getRoster().then(setData).catch((e) => toast.error(err(e, "Couldn't load"))), []);
  useEffect(() => { load(); }, [load]);
  const save = async (s, patch) => {
    const next = { user_ids: s.user_ids, duration_min: s.duration_min, ...patch };
    try { await setRosterSession(s.id, next); load(); } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!data.sessions.length) {
    return <div className="card-surface p-6 text-sm text-zinc-400">This gym has no sessions in FPA yet. Add them in <b>Settings → Session schedules</b> (day, time and name), then assign coaches here.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500">The usual week. Coaches are expected to be clocked in when their session starts; back-to-back sessions count as one block. For a one-off change (leave, cover), use <b>Today → Change for this day</b>.</div>
      {DAYS.map((label, d) => {
        const list = data.sessions.filter((s) => s.day_of_week === d);
        if (!list.length) return null;
        return (
          <div key={label} className="card-surface p-4 space-y-2" data-testid={`roster-day-${d}`}>
            <div className="text-sm font-semibold">{label}</div>
            {list.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm border-t border-[#1a1a1a] pt-2 first:border-0 first:pt-0">
                <span className="font-mono w-12 text-zinc-400">{s.start_time}</span>
                <span className="text-zinc-300 min-w-[120px]">{s.label}</span>
                <select value={s.duration_min} onChange={(e) => save(s, { duration_min: Number(e.target.value) })} className={field} aria-label="Length">
                  {[30, 45, 60, 75, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
                </select>
                {s.user_ids.map((u, i) => (
                  <span key={u} className="inline-flex items-center gap-1 rounded-full border border-[#2a2a2a] px-2 py-0.5 text-xs">
                    {s.coaches[i]}
                    <button onClick={() => save(s, { user_ids: s.user_ids.filter((x) => x !== u) })} className="text-zinc-500 hover:text-rose-400" aria-label={`Remove ${s.coaches[i]}`}>×</button>
                  </span>
                ))}
                <select value="" onChange={(e) => e.target.value && save(s, { user_ids: [...s.user_ids, e.target.value] })} className={field} data-testid={`roster-add-${s.id}`}>
                  <option value="">+ coach</option>
                  {staff.filter((p) => !s.user_ids.includes(p.user_id)).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function Month() {
  const [month, setMonth] = useState(todayLocal().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { getWorkMonth(month).then(setData).catch((e) => toast.error(err(e, "Couldn't load"))); }, [month]);
  const csv = () => {
    const head = ["Name", "Days worked", "Hours", "Shifts", "Late", "Late minutes", "Missed shifts", "Didn't clock out"];
    const lines = [head, ...(data?.rows || []).map((r) => [r.name, r.days_worked, r.hours, r.shifts, r.late, r.late_minutes, r.missed, r.forgot_out])];
    const blob = new Blob([lines.map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `staff-attendance-${month}.csv`; a.click();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={field} data-testid="work-month" />
        <Button variant="outline" onClick={csv} disabled={!data?.rows?.length} className="border-[#262626]"><Download className="h-4 w-4 mr-1.5" /> CSV for salary</Button>
      </div>
      {data && <div className="text-xs text-zinc-500">Up to {data.through}.</div>}
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
            {["Name", "Days", "Hours", "Shifts", "Late", "Missed", "No clock-out"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.user_id} className="border-b border-[#1a1a1a] last:border-0">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 font-mono">{r.days_worked}</td>
                <td className="px-3 py-2 font-mono">{r.hours}</td>
                <td className="px-3 py-2 font-mono">{r.shifts}</td>
                <td className={`px-3 py-2 font-mono ${r.late ? "text-amber-300" : ""}`}>{r.late}{r.late ? ` (${r.late_minutes} min)` : ""}</td>
                <td className={`px-3 py-2 font-mono ${r.missed ? "text-rose-300" : ""}`}>{r.missed}</td>
                <td className={`px-3 py-2 font-mono ${r.forgot_out ? "text-rose-300" : ""}`}>{r.forgot_out}</td>
              </tr>
            ))}
            {data && !data.rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">No shifts or clock-ins this month.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS = { missed: ["rose", "Not checked in"], late: ["amber", "Late"], waiting: ["zinc", "Not due yet"], on_time: ["emerald", "Checked in"], off: ["zinc", "Not due"] };
const WHY = { leave: "on leave", holiday: "holiday", sunday: "Sunday", exempt: "exempt", before_start: "not started yet" };

// Everyone, coaching or not: who's due today, who has checked in, and who their supervisor is.
function Everyone({ admin }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => getEveryoneToday().then(setD).catch((e) => toast.error(err(e, "Couldn't load"))), []);
  useEffect(() => { load(); }, [load]);
  const save = async (uid, body) => { try { await setWorkPerson(uid, body); toast.success("Saved"); load(); } catch (e) { toast.error(err(e, "Couldn't save")); } };
  if (!d) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="work-everyone">
      <div className="flex flex-wrap gap-2 text-xs">
        {["missed", "late", "waiting", "on_time", "off"].map((k) => <Badge key={k} tone={STATUS[k][0]}>{STATUS[k][1]}: {d.counts[k]}</Badge>)}
      </div>
      <div className="text-xs text-zinc-500">
        Due by their first session or shift (+15 min), or by 11:00 Monday to Saturday if they have none. Remote staff check in with a selfie.
        {d.mode !== "on" && <span className="text-amber-300"> Supervisor emails are {d.mode === "dry" ? "in test mode (worked out, not sent)" : "off"}.</span>}
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
            {["Name", "Gym", "Team", "Status", "Due", "In", "Email goes to", ...(admin ? ["Checks in", "Exempt"] : [])].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.user_id} className="border-b border-[#1a1a1a] last:border-0">
                <td className="px-3 py-2 font-medium">{r.name}{r.part_time && <span className="ml-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[11px] text-sky-300">Part-time</span>}</td>
                <td className="px-3 py-2 text-zinc-400">{(r.gym || "").replace(/^[A-Z]+ - /, "")}</td>
                <td className="px-3 py-2 text-zinc-400">{r.team}</td>
                <td className="px-3 py-2"><Badge tone={STATUS[r.status][0]}>{STATUS[r.status][1]}{r.why_not ? ` · ${WHY[r.why_not] || r.why_not}` : ""}</Badge></td>
                <td className="px-3 py-2 font-mono text-zinc-400">{r.due || "–"}</td>
                <td className="px-3 py-2 font-mono">{r.first_in || "–"}</td>
                <td className="px-3 py-2 text-zinc-400">{admin ? (
                  <select value={r.supervisor_ids[0] || ""} onChange={(e) => save(r.user_id, { supervisor_ids: e.target.value ? [e.target.value] : [] })} className={field}>
                    <option value="">{`Auto: ${r.supervisors.join(", ") || "none"}`}</option>
                    {d.people.filter((p) => p.user_id !== r.user_id).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                  </select>) : r.supervisors.join(", ")}{r.cc?.length ? <div className="text-[10px] text-zinc-500">cc {r.cc.join(", ")}</div> : null}</td>
                {admin && <td className="px-3 py-2">
                  <select value={r.remote_set ? (r.remote ? "remote" : "site") : "auto"} className={field}
                    onChange={(e) => save(r.user_id, { work_remote: e.target.value === "auto" ? null : e.target.value === "remote" })}>
                    <option value="auto">Auto ({r.remote ? "remote" : "at the gym"})</option><option value="remote">Remote</option><option value="site">At the gym</option>
                  </select></td>}
                {admin && <td className="px-3 py-2"><input type="checkbox" checked={r.exempt} onChange={(e) => save(r.user_id, { attendance_exempt: e.target.checked })} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Week by week: who checks in on time, by team and by person.
function Trends() {
  const [d, setD] = useState(null);
  const [weeks, setWeeks] = useState(8);
  useEffect(() => { setD(null); getCheckinTrends(weeks).then(setD).catch((e) => toast.error(err(e, "Couldn't load"))); }, [weeks]);
  const pct = (c) => (c.due ? Math.round((100 * c.on_time) / c.due) : null);
  const cell = (c) => {
    const v = pct(c);
    if (v == null) return <span className="text-zinc-700">–</span>;
    return <span className={`font-mono ${v >= 90 ? "text-emerald-300" : v >= 70 ? "text-amber-300" : "text-rose-300"}`} title={`${c.on_time} on time, ${c.late} late, ${c.missed} missed of ${c.due}`}>{v}%</span>;
  };
  const wk = (w) => new Date(`${w}T00:00:00`).toLocaleDateString([], { day: "numeric", month: "short" });
  return (
    <div className="space-y-4" data-testid="work-trends">
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className={field}>{[4, 8, 12, 26].map((n) => <option key={n} value={n}>Last {n} weeks</option>)}</select>
        On time = checked in by the due time. Weeks start Monday; today isn't counted.
      </div>
      {!d ? <div className="text-sm text-zinc-500">Working it out…</div> : (
        <>
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-3 py-2 font-medium">Team</th>{d.weeks.map((w) => <th key={w} className="px-2 py-2 font-medium text-right">{wk(w)}</th>)}</tr></thead>
              <tbody>{d.teams.map((t) => (
                <tr key={t.team} className="border-b border-[#1a1a1a] last:border-0"><td className="px-3 py-2 font-medium">{t.team}</td>
                  {t.weeks.map((c) => <td key={c.week} className="px-2 py-2 text-right">{cell(c)}</td>)}</tr>))}</tbody>
            </table>
          </div>
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
                {["Name", "Team", "Gym", "Due", "On time", "Late", "Missed", "Rate"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
                {d.weeks.map((w) => <th key={w} className="px-2 py-2 font-medium text-right">{wk(w)}</th>)}</tr></thead>
              <tbody>{d.people.map((r) => (
                <tr key={r.user_id} className="border-b border-[#1a1a1a] last:border-0">
                  <td className="px-3 py-2 font-medium">{r.name}</td><td className="px-3 py-2 text-zinc-400">{r.team}</td>
                  <td className="px-3 py-2 text-zinc-400">{(r.gym || "").replace(/^[A-Z]+ - /, "")}</td>
                  <td className="px-3 py-2 font-mono">{r.due}</td><td className="px-3 py-2 font-mono text-emerald-300">{r.on_time}</td>
                  <td className="px-3 py-2 font-mono text-amber-300">{r.late || ""}</td><td className="px-3 py-2 font-mono text-rose-300">{r.missed || ""}</td>
                  <td className="px-3 py-2">{r.rate == null ? "–" : cell({ due: r.due, on_time: r.on_time, late: r.late, missed: r.missed })}</td>
                  {r.weeks.map((c) => <td key={c.week} className="px-2 py-2 text-right">{cell(c)}</td>)}
                </tr>))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// QC team (CR-0058): everyone's clock-in and clock-out times at every gym, view only. Late (CR-0059): clocked in
// after their session or shift started, no grace; managers with no shift are due by 07:00 and 17:00.
function AllGyms() {
  const [day, setDay] = useState(todayLocal());
  const [gym, setGym] = useState("");
  const [lateOnly, setLateOnly] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); getQcDay(day, gym).then(setD).catch((e) => toast.error(err(e, "Couldn't load"))); }, [day, gym]);
  const rows = (d?.rows || []).filter((r) => !lateOnly || r.late.length);
  return (
    <div className="space-y-3" data-testid="work-all-gyms">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-44"><DatePicker value={day} onChange={(e) => setDay(e.target.value)} data-testid="qc-day" /></div>
        <select value={gym} onChange={(e) => setGym(e.target.value)} className={field} data-testid="qc-gym">
          <option value="">All gyms</option>
          {(d?.gyms || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-zinc-300">
          <input type="checkbox" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} data-testid="qc-late-only" /> Late only{d ? ` (${d.late})` : ""}
        </label>
      </div>
      <div className="text-xs text-zinc-500">Late = clocked in after their session or shift started, no grace period. Managers with no shift set are due by 07:00 and 17:00. Coaches with no session or shift that day aren&apos;t checked. Times are each gym&apos;s own time.</div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-[#1f1f1f]">
            {["Name", "Gym", "Role", "Due", "Clock in / out", ""].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.facility_id}-${r.user_id}`} className="border-b border-[#1a1a1a] last:border-0" data-testid={`qc-row-${r.user_id}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-zinc-400">{(r.gym || "").replace(/^[A-Z]+ - /, "")}</td>
                <td className="px-3 py-2 text-zinc-400">{r.role || "–"}</td>
                <td className="px-3 py-2 font-mono text-zinc-400">{r.shifts.length ? r.shifts.join(", ") : (r.expected.join(", ") || "–")}</td>
                <td className="px-3 py-2 font-mono">{r.punches.length ? r.punches.map((p) => `${p.kind === "in" ? "In" : "Out"} ${p.time}`).join(" · ") : <span className="text-rose-300">No clock-in</span>}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-1.5">
                  {r.late.map((l) => <Badge key={l.expected} tone="amber">Late {l.minutes} min · due {l.expected}</Badge>)}
                  {r.remote && <Badge tone="zinc">Remote</Badge>}
                </div></td>
              </tr>
            ))}
            {d && !rows.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-500">{lateOnly ? "No late clock-ins on this day." : "Nobody scheduled or clocked in on this day."}</td></tr>}
            {!d && <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Shifts({ staff, onSaved }) {
  const [edit, setEdit] = useState({});
  useEffect(() => { setEdit(Object.fromEntries(staff.map((s) => [s.user_id, JSON.parse(JSON.stringify(s.week || {}))]))); }, [staff]);
  const change = (uid, d, i, k, v) => setEdit((e) => {
    const w = { ...e[uid] }; const list = [...(w[d] || [])];
    if (k === "add") list.push({ start: "06:00", end: "11:00" });
    else if (k === "del") list.splice(i, 1);
    else list[i] = { ...list[i], [k]: v };
    w[d] = list; return { ...e, [uid]: w };
  });
  const save = async (uid) => {
    const week = Object.fromEntries(Object.entries(edit[uid] || {}).filter(([, l]) => l.length));
    try { await setWorkShifts(uid, week); toast.success("Shifts saved"); onSaved(); } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500">For people who don't run sessions (front desk, facility head). Coaches' hours come from the Roster. Add two shifts on a day for split shifts. Late = more than 10 minutes after the start.</div>
      {staff.map((s) => (
        <div key={s.user_id} className="card-surface p-4 space-y-2" data-testid={`shifts-${s.user_id}`}>
          <div className="flex items-center justify-between"><div className="font-medium">{s.name}</div>
            <Button size="sm" onClick={() => save(s.user_id)} className="bg-blue-600 hover:bg-blue-500">Save</Button></div>
          <div className="grid gap-1.5">
            {DAYS.map((label, d) => (
              <div key={label} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-10 text-zinc-500">{label}</span>
                {((edit[s.user_id] || {})[String(d)] || []).map((sh, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <input type="time" value={sh.start} onChange={(e) => change(s.user_id, String(d), i, "start", e.target.value)} className={field} />
                    <span className="text-zinc-600">–</span>
                    <input type="time" value={sh.end} onChange={(e) => change(s.user_id, String(d), i, "end", e.target.value)} className={field} />
                    <button onClick={() => change(s.user_id, String(d), i, "del")} className="text-zinc-600 hover:text-rose-400" aria-label="Remove shift"><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                <button onClick={() => change(s.user_id, String(d), 0, "add")} className="text-xs text-blue-400 inline-flex items-center"><Plus className="h-3 w-3" /> shift</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Setup({ location, onSaved }) {
  return (
    <div className="space-y-3">
      <div className="card-surface p-4 space-y-3">
        <div className="text-sm font-medium">How coaches clock in</div>
        <div className="text-xs text-zinc-400">They scan the QR on the front-desk tablet <b>and</b> their phone's location has to be at the gym.
          Both have to match, so a photo of the QR sent to someone outside doesn't work.</div>
        <Link to="/work/desk" className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm text-white" data-testid="open-desk-qr">
          Open the clock-in QR on this tablet
        </Link>
        <div className="text-xs text-zinc-500">On the front-desk tablet: sign in to FPA, open this, and leave it on screen.</div>
      </div>
      <Location location={location} onSaved={onSaved} />
    </div>
  );
}

function Location({ location, onSaved }) {
  const [radius, setRadius] = useState(location?.radius_m || 150);
  const [busy, setBusy] = useState(false);
  const setHere = async () => {
    setBusy(true);
    try {
      const pos = await position();
      if (pos.accuracy > 100) toast.warning(`Location is only accurate to ±${Math.round(pos.accuracy)} m. Try again inside the gym near a window for a better fix.`);
      await setWorkLocation({ lat: pos.lat, lng: pos.lng, radius_m: Number(radius) || 150 });
      toast.success("Gym location saved"); onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-4 space-y-3">
      <div className="text-sm">{location?.lat
        ? <>Set to {location.lat.toFixed(5)}, {location.lng.toFixed(5)} · clock-in allowed within {location.radius_m} m. <a className="text-blue-400" href={`https://maps.google.com/?q=${location.lat},${location.lng}`} target="_blank" rel="noreferrer">See on map</a></>
        : "Not set yet. Stand inside the gym and tap the button."}</div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-zinc-400">Allowed distance (m)</label>
        <input type="number" min={50} max={500} value={radius} onChange={(e) => setRadius(e.target.value)} className={`${field} w-24`} />
        <Button onClick={setHere} disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="set-location-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4 mr-1.5" />} Set to where I'm standing
        </Button>
      </div>
    </div>
  );
}

const phoneName = (ua) => {
  if (!ua) return "Phone";
  const m = ua.match(/\(([^;)]+);?\s*([^;)]*)/);
  const os = /iPhone/.test(ua) ? "iPhone" : /Android/.test(ua) ? (ua.match(/Android[^;]*;\s*([^;)]+)/)?.[1] || "Android phone") : (m?.[1] || "Device");
  const browser = /CriOS|Chrome/.test(ua) ? "Chrome" : /Safari/.test(ua) ? "Safari" : /Firefox/.test(ua) ? "Firefox" : "browser";
  return `${os.trim()} · ${browser}`;
};
const shortDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

function Phones() {
  const [data, setData] = useState(null);
  const load = useCallback(() => getWorkDevices().then(setData).catch(() => setData({ staff: [], pending: [] })), []);
  useEffect(() => { load(); }, [load]);
  const approve = async (d) => {
    if (!window.confirm(`Make this ${d.name}'s phone? Their old phone stops working for clock-in.`)) return;
    try { await approveWorkDevice(d.id); toast.success("Approved"); load(); } catch (e) { toast.error(err(e, "Couldn't approve")); }
  };
  const remove = async (d, what) => {
    if (!window.confirm(what)) return;
    try { await removeWorkDevice(d.id); toast.success("Done"); load(); } catch (e) { toast.error(err(e, "Couldn't remove")); }
  };
  if (!data) return <div className="card-surface p-6 text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="work-phones">
      <div className="text-xs text-zinc-500">Everyone clocks in on their own phone. The first phone someone uses becomes theirs; a different phone waits here for you to approve it.</div>
      {data.pending.length > 0 && (
        <div className="card-surface p-4 space-y-2 border-amber-500/30">
          <div className="text-sm font-medium text-amber-300">Waiting for approval</div>
          {data.pending.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-zinc-100">{d.name}</span>
              <span className="text-xs text-zinc-500">{phoneName(d.user_agent)} · asked {shortDay(d.requested_at)}</span>
              <span className="ml-auto flex gap-2">
                <Button onClick={() => approve(d)} className="h-8 bg-emerald-600 hover:bg-emerald-500" data-testid={`approve-phone-${d.id}`}>Approve</Button>
                <Button onClick={() => remove(d, `Reject this phone for ${d.name}?`)} className="h-8 bg-zinc-700 hover:bg-zinc-600">Reject</Button>
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="card-surface p-4 space-y-2">
        <div className="text-sm font-medium">Registered phones</div>
        {data.staff.map((s) => (
          <div key={s.user_id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-zinc-100">{s.name}</span>
            {s.phone
              ? <span className="text-xs text-zinc-500">{phoneName(s.phone.user_agent)} · since {shortDay(s.phone.created_at)}{s.phone.last_used ? ` · last used ${shortDay(s.phone.last_used)}` : ""}</span>
              : <span className="text-xs text-zinc-600">No phone yet: the first one they clock in with becomes theirs</span>}
            {s.phone && <button onClick={() => remove(s.phone, `Remove ${s.name}'s phone? The next phone they clock in with becomes theirs.`)}
              className="ml-auto text-xs text-zinc-500 hover:text-rose-400">Remove</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StaffAttendance() {
  const { effectiveRole, user } = useAuth();
  const isManager = atLeast(effectiveRole, "manager") || !!user?.nurture_manager || !!user?.accounts_head;
  const isAdmin = atLeast(user?.role, "admin");
  const isQc = isAdmin || !!user?.qc_access;
  const [tab, setTab] = useState(isManager ? "today" : "all");
  const [team, setTeam] = useState(null);
  const loadTeam = useCallback(() => getWorkStaff().then(setTeam).catch(() => {}), []);
  useEffect(() => { if (isManager) loadTeam(); }, [isManager, loadTeam]);
  const tabs = [
    ...(isManager ? [["today", "Today"], ["everyone", "Everyone today"], ["trends", "Trends"], ["roster", "Roster"], ["month", "Month"], ["shifts", "Other shifts"], ["phones", "Phones"], ["location", "Clock-in setup"]] : []),
    ...(isQc ? [["all", "All gyms"]] : []),
  ];
  return (
    <div className="space-y-6 pb-16" data-testid="staff-attendance-page">
      <div>
        <div className="eyebrow">Staff</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Clock in</h2>
      </div>
      <MyShift />
      {tabs.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1 border-b border-[#1f1f1f] pb-2">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`work-tab-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs ${tab === k ? "bg-zinc-500/15 text-zinc-100 border border-zinc-500/40" : "text-zinc-500 border border-transparent"}`}>{l}</button>
            ))}
          </div>
          {tab === "today" && <Today staff={team?.staff || []} />}
          {tab === "everyone" && <Everyone admin={isAdmin} />}
          {tab === "trends" && <Trends />}
          {tab === "roster" && <Roster staff={team?.staff || []} />}
          {tab === "month" && <Month />}
          {tab === "shifts" && <Shifts staff={team?.staff || []} onSaved={loadTeam} />}
          {tab === "phones" && <Phones />}
          {tab === "location" && <Setup location={team?.location} onSaved={loadTeam} />}
          {tab === "all" && isQc && <AllGyms />}
        </div>
      )}
    </div>
  );
}
